import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from '../common/Link';
import { DoubleMobileScroll } from '../project-detail/DoubleMobileScroll';
import { FullBleedVideo } from '../project-detail/FullBleedVideo';
import { TextSection } from '../project-detail/TextSection';
import { WideImage } from '../project-detail/WideImage';
import { useResize } from '../../hooks/viewport/useResize';
import { useSound } from '../../hooks/audio/useSound';
import { useAppStore } from '../../store/useAppStore';
import { CaseStudyBlock, Project, TransitionRect } from '../../types';

const isVideo = (src: string) => src.endsWith('.mp4') || src.endsWith('.webm');
const CASE_STUDY_OPEN_DELAY_MS = 600;
const BACKGROUND_SOFTEN_SCROLL_THRESHOLD = 48;
const CASE_STUDY_BACKGROUND = '#f7f7f7';

const splitDescription = (description: string) =>
  description
    .split(/<br\s*\/?><br\s*\/?>/i)
    .map(part => part.trim())
    .filter(Boolean);

const renderMedia = (src: string, title: string, className: string) => {
  if (isVideo(src)) {
    return (
      <video
        src={src}
        className={className}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-label={title}
      />
    );
  }

  return <img src={src} alt={title} className={className} loading="eager" />;
};

const getHeroTargetRect = (
  _project: Project,
  viewportWidth: number,
  viewportHeight: number
): TransitionRect => {
  return {
    left: 0,
    top: 0,
    width: viewportWidth,
    height: viewportHeight,
    borderRadius: 0,
  };
};

const buildDefaultBlocks = (project: Project): CaseStudyBlock[] => {
  const paragraphs = splitDescription(project.description);
  const additionalParagraphs = paragraphs.slice(1);
  const media = project.projectMedia ?? [];

  if (project.type === 'mobile') {
    return [
      {
        type: 'double-mobile-scroll',
        id: `${project.id}-mobile-story`,
        media: media.length > 0 ? media : [project.thumbnail],
        sections: (additionalParagraphs.length > 0
          ? additionalParagraphs
          : paragraphs
        ).map((body, index) => ({
          id: `${project.id}-section-${index}`,
          title:
            index === 0
              ? 'Designed for the hand'
              : index === 1
                ? 'Interaction details'
                : 'Refinement',
          body,
        })),
      },
    ];
  }

  const mediaBlocks = media.slice(1).map((item, index) =>
    isVideo(item)
      ? ({
          type: 'full-bleed-video',
          id: `${project.id}-video-${index}`,
          media: item,
          caption: `${project.title} motion study ${index + 1}`,
        } satisfies CaseStudyBlock)
      : ({
          type: 'wide-image',
          id: `${project.id}-image-${index}`,
          media: item,
          caption: `${project.title} still ${index + 1}`,
        } satisfies CaseStudyBlock)
  );

  return [
    ...(additionalParagraphs.length > 0
      ? [
          {
            type: 'text',
            id: `${project.id}-narrative`,
            title: 'Project Narrative',
            body: additionalParagraphs.join('<br /><br />'),
          } satisfies CaseStudyBlock,
        ]
      : []),
    ...mediaBlocks,
  ];
};

function ProjectHeroTransition({
  project,
  origin,
  isSoftened,
  viewportWidth,
  viewportHeight,
}: {
  project: Project;
  origin: TransitionRect | null;
  isSoftened: boolean;
  viewportWidth: number;
  viewportHeight: number;
}) {
  const heroMedia = project.projectMedia?.[0] ?? project.thumbnail;
  const target = getHeroTargetRect(project, viewportWidth, viewportHeight);
  const initial = origin ?? target;
  const targetCenterX = target.left + target.width / 2;
  const targetCenterY = target.top + target.height / 2;
  const initialCenterX = initial.left + initial.width / 2;
  const initialCenterY = initial.top + initial.height / 2;
  const deltaX = initialCenterX - targetCenterX;
  const deltaY = initialCenterY - targetCenterY;
  const initialScaleX = target.width > 0 ? initial.width / target.width : 1;
  const initialScaleY = target.height > 0 ? initial.height / target.height : 1;

  return (
    <div className="pointer-events-none fixed inset-0 z-[52] overflow-hidden">
      <div
        className="absolute overflow-hidden"
        style={{
          left: target.left,
          top: target.top,
          width: target.width,
          height: target.height,
        }}
      >
        <motion.div
          initial={{
            x: deltaX,
            y: deltaY,
            scaleX: initialScaleX,
            scaleY: initialScaleY,
            borderRadius: initial.borderRadius ?? 0,
          }}
          animate={{
            x: 0,
            y: 0,
            scaleX: 1,
            scaleY: 1,
            borderRadius: target.borderRadius ?? 0,
          }}
          exit={{
            x: deltaX,
            y: deltaY,
            scaleX: initialScaleX,
            scaleY: initialScaleY,
            borderRadius: initial.borderRadius ?? 0,
            opacity: 0,
          }}
          transition={{ duration: 0.98, ease: [0.16, 1, 0.3, 1] }}
          className="h-full w-full overflow-hidden bg-black shadow-[0_30px_120px_rgba(0,0,0,0.2)]"
          style={{
            transformOrigin: 'center center',
            willChange: 'transform, border-radius',
          }}
        >
          <motion.div
            animate={{
              scale: isSoftened ? 1.03 : 1,
              filter:
                project.type === 'desktop'
                  ? isSoftened
                    ? 'blur(8px) brightness(0.78) saturate(0.9)'
                    : 'blur(0px) brightness(1) saturate(1)'
                  : isSoftened
                    ? 'blur(2px) brightness(0.92) saturate(0.95)'
                    : 'blur(0px) brightness(1) saturate(1)',
            }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative h-full w-full"
          >
            {renderMedia(heroMedia, project.title, 'h-full w-full object-cover')}
          </motion.div>
          <motion.div
            className="absolute inset-0"
            animate={{
              backgroundColor:
                project.type === 'desktop'
                  ? isSoftened
                    ? 'rgba(247, 247, 247, 0.24)'
                    : 'rgba(247, 247, 247, 0)'
                  : isSoftened
                    ? 'rgba(247, 247, 247, 0.1)'
                    : 'rgba(247, 247, 247, 0)',
            }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          />
        </motion.div>
      </div>
    </div>
  );
}

function ProjectMeta({
  project,
  onLinkClick,
  onLinkHoverEnter,
  onLinkHoverLeave,
}: {
  project: Project;
  onLinkClick?: () => void;
  onLinkHoverEnter?: () => void;
  onLinkHoverLeave?: () => void;
}) {
  return (
    <div className="mx-auto grid w-full max-w-[1280px] gap-6 md:gap-8 xl:grid-cols-[minmax(0,0.92fr)_20rem] xl:gap-10">
      <div className="space-y-5 md:space-y-6">
        <h1 className="max-w-4xl heading-hero">
          {project.title}
        </h1>
        <TextSection body={splitDescription(project.description)[0] ?? project.description} />
      </div>

      <div className="space-y-5 md:space-y-6 rounded-[2rem] border border-black/6 bg-black/[0.025] p-6 md:p-8 backdrop-blur-sm">
        <div className="space-y-2">
          <p className="label">Year</p>
          <p className="text-lg text-black">{project.year}</p>
        </div>
        <div className="space-y-2">
          <p className="label">Context</p>
          <p className="text-lg text-black">{project.context}</p>
        </div>
        {project.links?.length ? (
          <div className="space-y-2">
            <p className="label">Links</p>
            <div className="flex flex-wrap gap-2">
              {project.links.map(link =>
                link.url && link.url !== 'N/A' ? (
                  <Link
                    key={`${project.id}-${link.label}`}
                    href={link.url}
                    isExternal
                    className="text-base"
                    onClick={onLinkClick}
                    onMouseEnter={onLinkHoverEnter}
                    onMouseLeave={onLinkHoverLeave}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <span
                    key={`${project.id}-${link.label}`}
                    className="body-muted text-black/50"
                  >
                    {link.label}
                  </span>
                )
              )}
            </div>
          </div>
        ) : null}
        <div className="space-y-2">
          <p className="label">Stack</p>
          <div className="flex flex-wrap gap-2">
            {project.techStack.map(tech => (
              <span
                key={tech}
                className="label rounded-full border border-black/10 px-3.5 py-1.5"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectBlocks({ project }: { project: Project }) {
  const blocks = useMemo(
    () =>
      project.caseStudy && project.caseStudy.length > 0
        ? project.caseStudy
        : buildDefaultBlocks(project),
    [project]
  );

  return (
    <div className="mx-auto w-full max-w-[1280px] space-y-12 md:space-y-14 lg:space-y-16">
      {blocks.map(block => {
        switch (block.type) {
          case 'text':
            return (
              <TextSection
                key={block.id}
                title={block.title}
                body={block.body}
              />
            );
          case 'wide-image':
            return (
              <WideImage
                key={block.id}
                src={block.media}
                alt={project.title}
                caption={block.caption}
                size={block.size}
              />
            );
          case 'full-bleed-video':
            return (
              <FullBleedVideo
                key={block.id}
                src={block.media}
                title={project.title}
                caption={block.caption}
                format={block.format ?? project.type}
              />
            );
          case 'double-mobile-scroll':
            return (
              <DoubleMobileScroll
                key={block.id}
                title={project.title}
                media={block.media}
                caption={block.caption}
                sections={block.sections}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

export function ProjectDetails() {
  const { playUI, playProjectCloseWind } = useSound();
  const selectedProject = useAppStore(state => state.selectedProject);
  const setSelected = useAppStore(state => state.setSelected);
  const transitionOrigin = useAppStore(state => state.transitionOrigin);
  const setTransitionOrigin = useAppStore(state => state.setTransitionOrigin);
  const isTransitionComplete = useAppStore(state => state.isTransitionComplete);
  const setGalleryScrollOffset = useAppStore(
    state => state.setGalleryScrollOffset
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const invalidate = useAppStore(state => state.invalidate);
  const { width, height } = useResize();
  const heroSpacerHeight = Math.max(height * 0.62, 420);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isBackgroundSoftened, setIsBackgroundSoftened] = useState(false);

  useEffect(() => {
    if (!selectedProject) return;

    setGalleryScrollOffset(0);
    setIsBackgroundSoftened(false);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [selectedProject, setGalleryScrollOffset]);

  useEffect(() => {
    if (!selectedProject) {
      setShowOverlay(false);
      return;
    }

    setShowOverlay(false);
    const timeoutId = window.setTimeout(() => {
      setShowOverlay(true);
    }, CASE_STUDY_OPEN_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [selectedProject]);

  useEffect(() => {
    if (!showOverlay) {
      setIsBackgroundSoftened(false);
    }
  }, [showOverlay]);

  useEffect(() => {
    if (!selectedProject || !showOverlay) return;

    const focusId = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(focusId);
  }, [selectedProject, showOverlay]);

  useEffect(() => {
    if (!selectedProject || !showOverlay) return;

    let rafId: number | null = null;
    let currentSpeed = 0;
    let targetDirection = 0;
    let lastFrameTime = 0;

    const MAX_SPEED = 1300; // px/s
    const ACCELERATION = 3400; // px/s^2
    const DECELERATION = 4200; // px/s^2

    const stopLoop = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      currentSpeed = 0;
      lastFrameTime = 0;
    };

    const tick = (timestamp: number) => {
      const container = scrollRef.current;
      if (!container) {
        stopLoop();
        return;
      }

      if (lastFrameTime === 0) {
        lastFrameTime = timestamp;
        rafId = window.requestAnimationFrame(tick);
        return;
      }

      const dt = Math.min((timestamp - lastFrameTime) / 1000, 0.033);
      lastFrameTime = timestamp;

      const targetSpeed = targetDirection * MAX_SPEED;
      if (targetDirection !== 0) {
        const delta = targetSpeed - currentSpeed;
        const step = Math.sign(delta) * Math.min(Math.abs(delta), ACCELERATION * dt);
        currentSpeed += step;
      } else {
        const decelStep = Math.min(Math.abs(currentSpeed), DECELERATION * dt);
        currentSpeed -= Math.sign(currentSpeed) * decelStep;
      }

      if (Math.abs(currentSpeed) > 0.1) {
        container.scrollTop += currentSpeed * dt;
        rafId = window.requestAnimationFrame(tick);
      } else {
        stopLoop();
      }
    };

    const ensureLoop = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(tick);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        targetDirection = 1;
        ensureLoop();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        targetDirection = -1;
        ensureLoop();
      } else if (event.key === 'PageDown') {
        const container = scrollRef.current;
        if (!container) return;
        const pageStep = Math.max(180, Math.floor(window.innerHeight * 0.72));
        event.preventDefault();
        container.scrollBy({ top: pageStep, behavior: 'smooth' });
      } else if (event.key === 'PageUp') {
        const container = scrollRef.current;
        if (!container) return;
        const pageStep = Math.max(180, Math.floor(window.innerHeight * 0.72));
        event.preventDefault();
        container.scrollBy({ top: -pageStep, behavior: 'smooth' });
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        targetDirection = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      stopLoop();
    };
  }, [selectedProject, showOverlay]);

  return (
    <AnimatePresence mode="wait">
      {selectedProject && showOverlay ? (
        <motion.div
          key={selectedProject.id}
          id="project-details"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="project-details-overlay fixed inset-0 z-50"
          style={{ backgroundColor: 'rgba(247, 247, 247, 0.75)' }}
        >
          <ProjectHeroTransition
            project={selectedProject}
            origin={transitionOrigin}
            isSoftened={isBackgroundSoftened}
            viewportWidth={width}
            viewportHeight={height}
          />

          <div className="pointer-events-none fixed left-1/2 top-5 z-[60] w-[min(92vw,1440px)] -translate-x-1/2 px-2 md:top-8">
            <motion.button
              ref={closeButtonRef}
              type="button"
              onClick={() => {
                playUI('close', 'click');
                playProjectCloseWind();
                setSelected(null, null);
                setTransitionOrigin(null);
              }}
              onMouseEnter={() => playUI('close', 'enter')}
              onMouseLeave={() => playUI('close', 'leave')}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ delay: 0.15, duration: 0.45 }}
              className="pointer-events-auto group ml-auto translate-x-1 grid h-12 w-12 place-items-center rounded-full border border-black/8 text-black shadow-[0_10px_30px_rgba(0,0,0,0.06)] backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:border-black/14 hover:bg-black/[0.06] hover:shadow-[0_14px_36px_rgba(0,0,0,0.1)] focus:outline-none focus-visible:border-black/35 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-black/65 focus-visible:ring-offset-2 focus-visible:ring-offset-[#f7f7f7]"
              style={{ backgroundColor: 'rgba(247, 247, 247, 0.88)' }}
              aria-label="Close details"
            >
              <svg
                className="h-5 w-5 transition-transform duration-300 group-hover:rotate-90"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </motion.button>
          </div>

          <div
            ref={scrollRef}
            className="case-study-scroll fixed inset-0 z-[53] overflow-y-scroll"
            onScroll={event => {
              setIsBackgroundSoftened(
                event.currentTarget.scrollTop > BACKGROUND_SOFTEN_SCROLL_THRESHOLD
              );
              invalidate?.();
            }}
          >
            <div style={{ height: `${heroSpacerHeight}px` }} />
            <motion.div
              initial={{ opacity: 0, y: 64 }}
              animate={
                isTransitionComplete
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: 64 }
              }
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-[56] mx-auto -mt-[10vh] min-h-screen w-[min(92vw,1440px)] rounded-[2rem] md:-mt-[12vh] md:rounded-[2.5rem] shadow-[0_-30px_80px_rgba(0,0,0,0.05)]"
              style={{ backgroundColor: CASE_STUDY_BACKGROUND }}
            >
              <div className="mx-auto flex min-h-full w-full flex-col gap-12 px-5 pb-14 pt-10 md:gap-14 md:px-8 md:pb-20 md:pt-14 lg:gap-16 lg:px-12 lg:pb-24 lg:pt-16">
                <ProjectMeta
                project={selectedProject}
                onLinkClick={() => playUI('link', 'click')}
                onLinkHoverEnter={() => playUI('link', 'enter')}
                onLinkHoverLeave={() => playUI('link', 'leave')}
              />
                <ProjectBlocks project={selectedProject} />
              </div>
            </motion.div>
            <div
              className="h-24 bg-transparent"
            />
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: isTransitionComplete ? 0 : 1,
            }}
            transition={{ duration: 0.35 }}
            className="pointer-events-none fixed inset-0 z-[55]"
            style={{ backgroundColor: 'rgba(247, 247, 247, 0.1)' }}
          >
            <div className="flex h-full items-end justify-center pb-10">
              <p className="label text-white/80 mix-blend-difference">
                Opening project
              </p>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
